import type { TableColumnConfigDto } from '@asset-metrix/portal-gateway-client-js';

import { toDateString } from '../../utils/dateHelper';

import {
  type AggregationMethod,
  filterOnlyLimitModeSchema,
  type LimitMode,
  type LimitType,
  type OperatorValue,
  parseFiniteNumberInput,
  parsePositiveIntegerInput,
  type PortfolioFilterCondition,
  type ValueType,
  type WizardStepKey,
} from './investmentLimitValidation';

export type {
  AggregationMethod,
  LimitMode,
  LimitType,
  OperatorValue,
  PortfolioFilterCondition,
  ValueType,
  WizardStepKey,
};

export type InvestmentLimitSourceColumn = {
  key: string;
  label: string;
};

export type InvestmentComplianceLimitDataLevel = {
  id: string;
  label: string;
  filters: InvestmentLimitSourceColumn[];
  currencyTypeIdKey?: string;
  currencyTypeLabelKey?: string;
  columns?: TableColumnConfigDto[];
};

export type DataLevelCode = string;
export type DataSourceConfiguration = {
  currencyType: string | null;
  dataLevelId: string | null;
  enabledFilterKeys: string[];
  filterValues: Record<string, string[]>;
};

export const POSITION_KPI = 'nbr_count';

export type LimitParameter = DataSourceConfiguration & {
  aggregationMethod: AggregationMethod | null;
  aggregationNumber: string;
  dataField: string | null;
  denominator: DataSourceConfiguration | null;
  evalMetric: string | null;
  evalOperator: OperatorValue | null;
  evalValue: string;
  limitValue: string;
  ruleOperator: OperatorValue | null;
  valueType: ValueType;
  warningThreshold: string;
};

export type LimitSetupDraft = {
  breachWorkflowApproval: boolean;
  description: string;
  limitMode: LimitMode;
  limitName: string;
  limitType: LimitType | null;
  parameter: LimitParameter;
  validFrom: Date | null;
  validIndefinitely: boolean;
  validTo: Date | null;
};

export type LimitRecordPayload = {
  limit_name: string;
  description: string | null;
  limit_category: LimitType | null;
  limit_type: LimitMode;
  comparison_type: ValueType | null;
  start_date: string | null;
  end_date: string | null;
  breach_approval_required: boolean;
  data_level: DataLevelCode | null;
  currency_type: string | null;
  aggregation_method: string | null;
  aggregation_number: number | null;
  portfolio_filters: string | null;
  kpi: string | null;
  limit_operator: OperatorValue | null;
  limit_value: number | null;
  limit_value_warn: number | null;
  eval_metric: string | null;
  eval_operator: OperatorValue | null;
  eval_value: number | null;
  ref_data_level: DataLevelCode | null;
  ref_currency_type: string | null;
  ref_portfolio_filters: string | null;
};

export const limitModeOptions = [
  { icon: 'functions', id: 'aggregation', value: 'Aggregation' },
  { icon: 'rule', id: 'position', value: 'Position' },
  { icon: 'block', id: 'exclusion', value: 'Exclusion' },
  { icon: 'check_circle', id: 'inclusion', value: 'Inclusion' },
] as const satisfies readonly {
  icon: string;
  id: 'aggregation' | 'exclusion' | 'inclusion' | 'position';
  value: LimitMode;
}[];

export const limitTypeOptions = [
  { id: 'regulatory', value: 'Regulatory' },
  { id: 'contractual', value: 'Contractual' },
  { id: 'client_specific', value: 'Client Specific' },
  { id: 'internal', value: 'Internal' },
] as const satisfies readonly {
  id: 'regulatory' | 'contractual' | 'client_specific' | 'internal';
  value: LimitType;
}[];

export const dataFieldOptions = [
  'available_capital',
  'cumulative_all_commitment',
  'cumulative_all_cost',
  'cumulative_all_paid_in_inside_commitment',
  'cumulative_all_returned',
  'days_to_final_termination_date',
  'days_to_investment_end_date',
  'days_to_termination_date',
  'dpi',
  'effective_valuation',
  'exposure',
  'irr',
  'rvpi',
  'tvpi',
  'undrawn_commitment',
] as const;

export const valueTypeOptions: Array<{
  icon: string;
  value: ValueType;
}> = [
  { icon: 'tag', value: 'absolute' },
  { icon: 'percent', value: 'relative' },
];

export const aggregationMethodOptions = [
  { id: 'bottomN', value: 'Bottom N' },
  { id: 'sum', value: 'Sum' },
  { id: 'topN', value: 'Top N' },
] as const satisfies readonly {
  id: 'bottomN' | 'sum' | 'topN';
  value: AggregationMethod;
}[];

export const operatorValueOptions: Array<{
  label: string;
  value: OperatorValue;
}> = [
  { label: '<=', value: 'le' },
  { label: '<', value: 'lt' },
  { label: '>=', value: 'ge' },
  { label: '>', value: 'gt' },
  { label: '=', value: 'eq' },
  { label: '!=', value: 'ne' },
];

export function formatOperatorLabel(operator: OperatorValue | null) {
  if (operator == null) {
    return null;
  }

  return (
    operatorValueOptions.find((option) => option.value === operator)?.label ??
    operator
  );
}

export function getWizardSteps(includeDenominator: boolean) {
  return [
    { key: 'overview' as const, label: 'General settings' },
    { key: 'parameter' as const, label: 'Parameter' },
    ...(includeDenominator
      ? [{ key: 'denominator' as const, label: 'Denominator' }]
      : []),
    { key: 'workflow' as const, label: 'Workflow & validity' },
    { key: 'review' as const, label: 'Summary' },
  ] satisfies Array<{ key: WizardStepKey; label: string }>;
}

export function createDataSourceConfiguration(): DataSourceConfiguration {
  return {
    currencyType: null,
    dataLevelId: null,
    enabledFilterKeys: [],
    filterValues: {},
  };
}

export function createLimitParameter(): LimitParameter {
  return {
    ...createDataSourceConfiguration(),
    aggregationMethod: null,
    aggregationNumber: '',
    dataField: null,
    denominator: null,
    evalMetric: null,
    evalOperator: null,
    evalValue: '',
    limitValue: '',
    ruleOperator: null,
    valueType: 'absolute',
    warningThreshold: '',
  };
}

export function createLimitDraft(): LimitSetupDraft {
  return {
    breachWorkflowApproval: false,
    description: '',
    limitMode: 'Aggregation',
    limitName: '',
    limitType: 'Internal',
    parameter: createLimitParameter(),
    validFrom: new Date(),
    validIndefinitely: true,
    validTo: null,
  };
}

export function isFilterOnlyLimit(mode: LimitMode) {
  return filterOnlyLimitModeSchema.safeParse(mode).success;
}

export function percentExpressionField(
  limitMode: LimitMode,
  valueType: ValueType
): 'evalValue' | 'limitValue' | null {
  if (valueType !== 'relative' || isFilterOnlyLimit(limitMode)) {
    return null;
  }

  return limitMode === 'Position' ? 'evalValue' : 'limitValue';
}

export function needsDenominator(
  draft: Pick<LimitSetupDraft, 'limitMode' | 'parameter'>
) {
  return (
    !isFilterOnlyLimit(draft.limitMode) &&
    draft.parameter.valueType === 'relative'
  );
}

export function clearFieldsForLimitMode(
  parameter: LimitParameter,
  limitMode: LimitMode
): LimitParameter {
  const filterOnly = isFilterOnlyLimit(limitMode);

  return {
    ...parameter,
    aggregationMethod:
      limitMode === 'Aggregation' ? parameter.aggregationMethod : null,
    aggregationNumber:
      limitMode === 'Aggregation' ? parameter.aggregationNumber : '',
    dataField: filterOnly
      ? null
      : limitMode === 'Position'
        ? POSITION_KPI
        : parameter.dataField === POSITION_KPI
          ? null
          : parameter.dataField,
    evalMetric: limitMode === 'Position' ? parameter.evalMetric : null,
    evalOperator: limitMode === 'Position' ? parameter.evalOperator : null,
    evalValue: limitMode === 'Position' ? parameter.evalValue : '',
    denominator: filterOnly ? null : parameter.denominator,
    limitValue: filterOnly ? '' : parameter.limitValue,
    ruleOperator: filterOnly ? null : parameter.ruleOperator,
    valueType: filterOnly ? 'absolute' : parameter.valueType,
    warningThreshold: filterOnly ? '' : parameter.warningThreshold,
  };
}

export function applyParameterUpdate(
  parameter: LimitParameter,
  update: Partial<LimitParameter>
): LimitParameter {
  const next = { ...parameter, ...update };
  if (next.aggregationMethod === 'Sum') {
    next.aggregationNumber = '';
  }
  return next;
}

function toExpressionPayloadValue(value: string, asPercent: boolean) {
  const parsed = parseFiniteNumberInput(value);
  if (parsed == null) {
    return null;
  }

  return asPercent ? parsed / 100 : parsed;
}

function buildPortfolioFilters(
  source: DataSourceConfiguration,
  invert = false
): string | null {
  const filters = source.enabledFilterKeys.flatMap<PortfolioFilterCondition>(
    (key): PortfolioFilterCondition[] => {
      const values = source.filterValues[key] ?? [];
      if (values.length === 0) {
        return [];
      }
      return values.length === 1
        ? [{ field: key, op: invert ? 'ne' : 'eq', value: values[0] }]
        : [{ field: key, op: invert ? 'notin' : 'in', value: values }];
    }
  );

  return filters.length > 0 ? JSON.stringify(filters) : null;
}

function formatTransferDate(date: Date | null) {
  return date == null || Number.isNaN(date.getTime())
    ? null
    : toDateString(date);
}

export function buildLimitRecordPayload(
  draft: LimitSetupDraft
): LimitRecordPayload {
  const parameter = draft.parameter;
  const isRelative = needsDenominator(draft);
  const filterOnly = isFilterOnlyLimit(draft.limitMode);
  const isPosition = draft.limitMode === 'Position';
  const isAggregation = draft.limitMode === 'Aggregation';
  const invertFilters = draft.limitMode === 'Inclusion';
  const denominator =
    isRelative && parameter.denominator != null ? parameter.denominator : null;
  const percentField = percentExpressionField(
    draft.limitMode,
    parameter.valueType
  );

  return {
    limit_name: draft.limitName.trim(),
    description: draft.description.trim() || null,
    limit_category: draft.limitType,
    limit_type: draft.limitMode,
    comparison_type: filterOnly ? null : draft.parameter.valueType,
    start_date: formatTransferDate(draft.validFrom),
    end_date: draft.validIndefinitely
      ? null
      : formatTransferDate(draft.validTo),
    breach_approval_required: draft.breachWorkflowApproval,
    data_level: parameter.dataLevelId,
    currency_type: parameter.currencyType,
    aggregation_method: isAggregation ? parameter.aggregationMethod : null,
    aggregation_number:
      isAggregation &&
      parameter.aggregationMethod !== 'Sum' &&
      parameter.aggregationMethod != null
        ? parsePositiveIntegerInput(parameter.aggregationNumber)
        : null,
    portfolio_filters: buildPortfolioFilters(parameter, invertFilters),
    kpi: filterOnly ? null : parameter.dataField,
    limit_operator: filterOnly ? null : parameter.ruleOperator,
    limit_value: filterOnly
      ? null
      : toExpressionPayloadValue(
          parameter.limitValue,
          percentField === 'limitValue'
        ),
    limit_value_warn: filterOnly
      ? null
      : toExpressionPayloadValue(parameter.warningThreshold, true),
    eval_metric: isPosition ? parameter.evalMetric : null,
    eval_operator: isPosition ? parameter.evalOperator : null,
    eval_value: isPosition
      ? toExpressionPayloadValue(
          parameter.evalValue,
          percentField === 'evalValue'
        )
      : null,
    ref_data_level: denominator?.dataLevelId ?? null,
    ref_currency_type: denominator?.currencyType ?? null,
    ref_portfolio_filters:
      denominator == null
        ? null
        : buildPortfolioFilters(denominator, invertFilters),
  };
}

export function buildLimitRowData(
  payload: LimitRecordPayload,
  columnKeys?: readonly string[]
): Record<string, string | number | boolean> {
  const entries = Object.entries(payload).filter(
    (entry): entry is [string, string | number | boolean] => entry[1] != null
  );
  if (columnKeys == null) {
    return Object.fromEntries(entries);
  }

  const allowed = new Set(columnKeys);
  return Object.fromEntries(entries.filter(([key]) => allowed.has(key)));
}
